import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Settings, Calculator, Home, Building2, Users, Euro, Leaf,
  CheckCircle2, AlertCircle, RefreshCw, History, ChevronDown, ChevronRight,
  Thermometer, Wind, Sun, Droplets, Shield, FileText, Save, Trash2, Eye,
  Download, Calendar, RotateCcw, XCircle, Info, HelpCircle, Banknote, Wallet,
  TrendingUp, Award, BadgeCheck, BadgeAlert, BadgeX, Sparkles, Accessibility,
  Heart, Lightbulb, DoorOpen
} from 'lucide-react';
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Icônes pour les types de travaux - Rénovation énergétique
const WORK_TYPE_ICONS = {
  pompe_chaleur: Thermometer,
  pac_geothermie: Thermometer,
  chaudiere_biomasse: Thermometer,
  poele_bois: Thermometer,
  poele_granules: Thermometer,
  chauffe_eau_solaire: Sun,
  chauffe_eau_thermodynamique: Droplets,
  isolation_combles: Shield,
  isolation_murs: Shield,
  isolation_planchers: Shield,
  fenetres: Wind,
  ventilation: Wind,
  audit_energetique: FileText,
  // Travaux d'adaptation MaPrimeAdapt'
  douche_accessible: Droplets,
  monte_escalier: TrendingUp,
  elargissement_portes: DoorOpen,
  wc_adapte: Home,
  barre_appui: Shield,
  rampe_acces: TrendingUp,
  volets_motorises: Wind,
  chemin_lumineux: Lightbulb,
  revetement_antiderapant: Shield
};

// Types de travaux d'adaptation MaPrimeAdapt'
const ADAPTATION_WORKS = {
  douche_accessible: { label: "Douche accessible / italienne", category: "Salle de bain" },
  monte_escalier: { label: "Monte-escalier", category: "Mobilité" },
  elargissement_portes: { label: "Élargissement des portes", category: "Mobilité" },
  wc_adapte: { label: "WC adapté / rehaussé", category: "Salle de bain" },
  barre_appui: { label: "Barres d'appui", category: "Sécurité" },
  rampe_acces: { label: "Rampe d'accès", category: "Mobilité" },
  volets_motorises: { label: "Volets motorisés", category: "Confort" },
  chemin_lumineux: { label: "Chemin lumineux", category: "Sécurité" },
  revetement_antiderapant: { label: "Revêtement antidérapant", category: "Sécurité" }
};

// Couleurs des catégories de revenus - style moderne avec dégradés
const INCOME_CATEGORY_COLORS = {
  bleu: { 
    bg: 'bg-gradient-to-r from-blue-500 to-blue-600', 
    text: 'text-white', 
    border: 'border-blue-400', 
    label: 'Très modestes',
    icon: '💙',
    description: 'Vous bénéficiez des aides maximales'
  },
  jaune: { 
    bg: 'bg-gradient-to-r from-yellow-500 to-amber-500', 
    text: 'text-white', 
    border: 'border-yellow-400', 
    label: 'Modestes',
    icon: '💛',
    description: 'Vous bénéficiez d\'aides importantes'
  },
  violet: { 
    bg: 'bg-gradient-to-r from-violet-500 to-purple-600', 
    text: 'text-white', 
    border: 'border-violet-400', 
    label: 'Intermédiaires',
    icon: '💜',
    description: 'Vous bénéficiez d\'aides intermédiaires'
  },
  rose: { 
    bg: 'bg-gradient-to-r from-rose-500 to-pink-600', 
    text: 'text-white', 
    border: 'border-rose-400', 
    label: 'Supérieurs',
    icon: '💗',
    description: 'Vous bénéficiez d\'aides ciblées'
  }
};

// Composant pour les sections repliables
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-slate-800/50 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
          <span className="text-sm font-medium text-slate-300">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-4 py-3 bg-slate-900/30 text-sm text-slate-400">
          {children}
        </div>
      )}
    </div>
  );
};

const SimulateurAidesDashboard = ({ onBack, authUser }) => {
  // States
  const [activeTab, setActiveTab] = useState('simulateur');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [savingKey, setSavingKey] = useState(false);
  const [showAdaptation, setShowAdaptation] = useState(false); // Toggle pour MaPrimeAdapt'
  
  // Form state with default values
  const [formData, setFormData] = useState({
    beneficiary_type: 'particulier',
    income_rfr: '25000',
    household_size: '3',
    department: '75',
    housing_type: 'maison',
    housing_status: 'proprietaire',
    housing_age: '25',
    housing_surface: '120',
    work_types: [],
    // Champs MaPrimeAdapt'
    has_elderly: false,      // Personne de 60+ ans
    has_disability: false,   // Personne en situation de handicap
    gir_level: null          // Niveau GIR (1-6) si perte d'autonomie
  });

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await authFetch(`${API}/mesubventions/config`);
      const data = await response.json();
      setConfig(data);
      setLoading(false);
      
      // Si pas de clé API, afficher les paramètres
      if (!data.has_api_key) {
        setShowSettings(true);
      }
    } catch (err) {
      console.error('Erreur chargement config:', err);
      setError('Impossible de charger la configuration');
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await authFetch(`${API}/mesubventions/history?limit=20`);
      const data = await response.json();
      setHistory(data.simulations || []);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await authFetch(`${API}/mesubventions/test-connection`);
      const data = await response.json();
      setConnectionStatus(data);
    } catch (err) {
      setConnectionStatus({ success: false, error: 'Erreur de connexion' });
    }
    setTestingConnection(false);
  };

  const saveApiKey = async () => {
    if (!apiKey.trim()) return;
    
    setSavingKey(true);
    try {
      const response = await authFetch(`${API}/mesubventions/config/api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey.trim() })
      });
      
      if (response.ok) {
        setApiKey('');
        await loadConfig();
        await testConnection();
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde de la clé');
    }
    setSavingKey(false);
  };

  const toggleWorkType = (workType) => {
    setFormData(prev => ({
      ...prev,
      work_types: prev.work_types.includes(workType)
        ? prev.work_types.filter(t => t !== workType)
        : [...prev.work_types, workType]
    }));
  };

  const calculateAids = async () => {
    setCalculating(true);
    setResult(null);
    setError(null);
    
    // Préparer les données avec les bons types (parseInt pour les nombres)
    const requestData = {
      beneficiary_type: formData.beneficiary_type || 'particulier',
      income_rfr: parseInt(formData.income_rfr, 10) || 0,
      household_size: parseInt(formData.household_size, 10) || 1,
      department: formData.department || '',
      housing_type: formData.housing_type || 'maison',
      housing_status: formData.housing_status || 'proprietaire',
      housing_age: parseInt(formData.housing_age, 10) || 0,
      housing_surface: parseInt(formData.housing_surface, 10) || 0,
      work_types: formData.work_types || [],
      // Champs MaPrimeAdapt'
      has_elderly: formData.has_elderly || false,
      has_disability: formData.has_disability || false,
      gir_level: formData.gir_level ? parseInt(formData.gir_level, 10) : null
    };
    
    try {
      // Appel via notre backend (proxy pour éviter CORS)
      const response = await authFetch(`${API}/mesubventions/calculate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        console.log('✅ Calcul MesSubventions réussi:', data);
      } else {
        setError(data.error || data.detail || 'Erreur lors du calcul des aides');
      }
    } catch (err) {
      console.error('❌ Erreur appel API MesSubventions:', err);
      setError('Erreur de connexion au serveur. Vérifiez votre connexion.');
    }
    
    setCalculating(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  // Grouper les types de travaux par catégorie
  const workTypesByCategory = config?.work_types 
    ? Object.entries(config.work_types).reduce((acc, [key, value]) => {
        const category = value.category;
        if (!acc[category]) acc[category] = [];
        acc[category].push({ key, ...value });
        return acc;
      }, {})
    : {};

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-lime-900/50 to-emerald-900/50 border-b border-lime-500/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-6 h-6 text-lime-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Home className="w-7 h-7 text-lime-400" />
                  Simulateur d'Aides Rénovation
                </h1>
                <p className="text-lime-300/70 text-sm">Calculez les aides MaPrimeRénov', CEE et autres subventions</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-lime-500 text-slate-900' : 'hover:bg-white/10 text-lime-400'}`}
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setActiveTab('simulateur')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'simulateur'
                  ? 'bg-lime-500 text-slate-900'
                  : 'text-lime-300 hover:bg-white/10'
              }`}
              data-testid="tab-simulateur"
            >
              <Calculator className="w-4 h-4 inline mr-2" />
              Simulateur
            </button>
            <button
              onClick={() => { setActiveTab('historique'); loadHistory(); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'historique'
                  ? 'bg-lime-500 text-slate-900'
                  : 'text-lime-300 hover:bg-white/10'
              }`}
              data-testid="tab-historique"
            >
              <History className="w-4 h-4 inline mr-2" />
              Historique
            </button>
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-slate-800 border-b border-slate-700">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-lime-400" />
              Configuration API MesSubventions.fr
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* API Key */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300">
                  Clé API
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={config?.has_api_key ? '••••••••' : 'Entrez votre clé API'}
                    className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                    data-testid="api-key-input"
                  />
                  <button
                    onClick={saveApiKey}
                    disabled={!apiKey.trim() || savingKey}
                    className="px-4 py-2 bg-lime-500 text-slate-900 rounded-lg font-medium hover:bg-lime-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="save-api-key-btn"
                  >
                    {savingKey ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Sauvegarder
                  </button>
                </div>
                {config?.api_key_preview && (
                  <p className="text-xs text-slate-500">Clé actuelle : {config.api_key_preview}</p>
                )}
              </div>
              
              {/* Connection Test */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300">
                  Test de connexion
                </label>
                <div className="flex gap-2 items-start">
                  <button
                    onClick={testConnection}
                    disabled={testingConnection || !config?.has_api_key}
                    className="px-4 py-2 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-600 disabled:opacity-50 flex items-center gap-2"
                    data-testid="test-connection-btn"
                  >
                    {testingConnection ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Tester
                  </button>
                  
                  {connectionStatus && (
                    <div className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      connectionStatus.success 
                        ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-red-900/50 text-red-400 border border-red-500/30'
                    }`}>
                      {connectionStatus.success ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <AlertCircle className="w-4 h-4" />
                      )}
                      <span className="text-sm">
                        {connectionStatus.success ? connectionStatus.message : connectionStatus.error}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'simulateur' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Formulaire */}
            <div className="space-y-6">
              {/* Informations bénéficiaire */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-lime-400" />
                  Informations du bénéficiaire
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Revenu fiscal de référence (€)
                    </label>
                    <input
                      type="number"
                      value={formData.income_rfr}
                      onChange={(e) => setFormData(prev => ({ ...prev, income_rfr: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      placeholder="25000"
                      data-testid="income-rfr-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Nombre de personnes au foyer
                    </label>
                    <input
                      type="number"
                      value={formData.household_size}
                      onChange={(e) => setFormData(prev => ({ ...prev, household_size: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      placeholder="3"
                      min="1"
                      max="10"
                      data-testid="household-size-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Département (code)
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      placeholder="75"
                      maxLength={3}
                      data-testid="department-input"
                    />
                  </div>
                </div>
              </div>

              {/* Informations logement */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-lime-400" />
                  Informations du logement
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Type de logement
                    </label>
                    <select
                      value={formData.housing_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, housing_type: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      data-testid="housing-type-select"
                    >
                      <option value="maison">Maison individuelle</option>
                      <option value="appartement">Appartement</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Statut d'occupation
                    </label>
                    <select
                      value={formData.housing_status}
                      onChange={(e) => setFormData(prev => ({ ...prev, housing_status: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      data-testid="housing-status-select"
                    >
                      <option value="proprietaire">Propriétaire occupant</option>
                      <option value="bailleur">Propriétaire bailleur</option>
                      <option value="locataire">Locataire</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Âge du logement (années)
                    </label>
                    <input
                      type="number"
                      value={formData.housing_age}
                      onChange={(e) => setFormData(prev => ({ ...prev, housing_age: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      placeholder="25"
                      min="0"
                      data-testid="housing-age-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Surface habitable (m²)
                    </label>
                    <input
                      type="number"
                      value={formData.housing_surface}
                      onChange={(e) => setFormData(prev => ({ ...prev, housing_surface: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-lime-500 focus:ring-1 focus:ring-lime-500"
                      placeholder="120"
                      min="0"
                      data-testid="housing-surface-input"
                    />
                  </div>
                </div>
              </div>

              {/* Types de travaux */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-lime-400" />
                  Types de travaux envisagés
                </h3>
                
                <div className="space-y-4">
                  {Object.entries(workTypesByCategory).map(([category, types]) => (
                    <div key={category}>
                      <h4 className="text-sm font-medium text-slate-400 mb-2">{category}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {types.map(({ key, label }) => {
                          const IconComponent = WORK_TYPE_ICONS[key] || Leaf;
                          const isSelected = formData.work_types.includes(key);
                          return (
                            <button
                              key={key}
                              onClick={() => toggleWorkType(key)}
                              className={`p-3 rounded-lg border text-left flex items-center gap-2 transition-all ${
                                isSelected
                                  ? 'bg-lime-500/20 border-lime-500 text-lime-300'
                                  : 'bg-slate-900/50 border-slate-600 text-slate-300 hover:border-slate-500'
                              }`}
                              data-testid={`work-type-${key}`}
                            >
                              <IconComponent className={`w-4 h-4 ${isSelected ? 'text-lime-400' : 'text-slate-500'}`} />
                              <span className="text-sm">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                
                {formData.work_types.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-sm text-slate-400">
                      {formData.work_types.length} travaux sélectionnés
                    </p>
                  </div>
                )}
              </div>

              {/* Section MaPrimeAdapt' - Adaptation du logement */}
              <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-indigo-500/30">
                <button 
                  onClick={() => setShowAdaptation(!showAdaptation)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="toggle-adaptation-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                      <Heart className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">MaPrimeAdapt'</h3>
                      <p className="text-indigo-300/70 text-sm">Adaptation au vieillissement ou handicap</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-indigo-400 transition-transform ${showAdaptation ? 'rotate-180' : ''}`} />
                </button>
                
                {showAdaptation && (
                  <div className="mt-6 space-y-6">
                    {/* Conditions d'éligibilité */}
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-indigo-300 mb-3">Situation du bénéficiaire</h4>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.has_elderly}
                            onChange={(e) => setFormData(prev => ({ ...prev, has_elderly: e.target.checked }))}
                            className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500"
                            data-testid="has-elderly-checkbox"
                          />
                          <span className="text-slate-300 group-hover:text-white transition-colors">
                            👴 Personne de <strong>60 ans ou plus</strong> dans le foyer
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.has_disability}
                            onChange={(e) => setFormData(prev => ({ ...prev, has_disability: e.target.checked }))}
                            className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500"
                            data-testid="has-disability-checkbox"
                          />
                          <span className="text-slate-300 group-hover:text-white transition-colors">
                            ♿ Personne en <strong>situation de handicap</strong>
                          </span>
                        </label>
                        
                        <div className="pt-2">
                          <label className="block text-sm text-slate-400 mb-2">
                            Niveau GIR (si perte d'autonomie)
                          </label>
                          <select
                            value={formData.gir_level || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gir_level: e.target.value || null }))}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            data-testid="gir-level-select"
                          >
                            <option value="">Non concerné</option>
                            <option value="1">GIR 1 - Dépendance totale</option>
                            <option value="2">GIR 2 - Dépendance forte</option>
                            <option value="3">GIR 3 - Dépendance moyenne</option>
                            <option value="4">GIR 4 - Dépendance légère</option>
                            <option value="5">GIR 5 - Autonome avec aide ponctuelle</option>
                            <option value="6">GIR 6 - Autonome</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Travaux d'adaptation */}
                    {(formData.has_elderly || formData.has_disability || formData.gir_level) && (
                      <div>
                        <h4 className="text-sm font-medium text-indigo-300 mb-3">Travaux d'adaptation</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(ADAPTATION_WORKS).map(([key, { label }]) => {
                            const IconComponent = WORK_TYPE_ICONS[key] || Heart;
                            const isSelected = formData.work_types.includes(key);
                            return (
                              <button
                                key={key}
                                onClick={() => toggleWorkType(key)}
                                className={`p-3 rounded-lg border text-left flex items-center gap-2 transition-all ${
                                  isSelected
                                    ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300'
                                    : 'bg-slate-900/50 border-slate-600 text-slate-300 hover:border-slate-500'
                                }`}
                                data-testid={`work-type-${key}`}
                              >
                                <IconComponent className={`w-4 h-4 ${isSelected ? 'text-indigo-400' : 'text-slate-500'}`} />
                                <span className="text-sm">{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Info barèmes MaPrimeAdapt' */}
                    <div className="bg-indigo-900/20 rounded-lg p-3 border border-indigo-500/20">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-indigo-300/80">
                          <p className="font-medium mb-1">Barèmes MaPrimeAdapt' 2026 :</p>
                          <p>• Très modestes : <strong>70%</strong> (plafond 22 000 € HT)</p>
                          <p>• Modestes : <strong>50%</strong> (plafond 22 000 € HT)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bouton calculer */}
              <button
                onClick={calculateAids}
                disabled={calculating || formData.work_types.length === 0}
                className="w-full py-4 bg-gradient-to-r from-lime-500 to-emerald-500 text-slate-900 rounded-xl font-bold text-lg hover:from-lime-400 hover:to-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all"
                data-testid="calculate-btn"
              >
                {calculating ? (
                  <>
                    <RefreshCw className="w-6 h-6 animate-spin" />
                    Calcul en cours...
                  </>
                ) : (
                  <>
                    <Calculator className="w-6 h-6" />
                    Calculer les aides
                  </>
                )}
              </button>
              
            </div>

            {/* Résultats - Style MesSubventions.fr */}
            <div className="space-y-6">
              {error && (
                <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-300 font-medium">Erreur</p>
                    <p className="text-red-400/80 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {result && (
                <>
                  {/* 1. En-tête avec 3 cards horizontales */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* Card 1: Subventions directes (fond vert) */}
                    <div className="bg-gradient-to-br from-emerald-600 to-green-700 rounded-xl p-5 text-white">
                      <div className="flex items-center gap-2 mb-3">
                        <Euro className="w-5 h-5 text-emerald-200" />
                        <span className="text-emerald-100 text-sm font-medium">Subventions directes</span>
                      </div>
                      <p className="text-3xl font-bold mb-1">
                        {formatCurrency(result.summary?.total_subventions || 0)}
                      </p>
                      <p className="text-emerald-200 text-xs">versées sur votre compte</p>
                    </div>
                    
                    {/* Card 2: Reste à charge (fond blanc, texte orange) */}
                    <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-slate-600">
                      <div className="flex items-center gap-2 mb-3">
                        <Home className="w-5 h-5 text-orange-400" />
                        <span className="text-slate-300 text-sm font-medium">Reste à charge</span>
                      </div>
                      <p className="text-3xl font-bold text-orange-400 mb-1">
                        {formatCurrency(result.summary?.reste_a_charge || 0)}
                      </p>
                      <p className="text-slate-400 text-xs">après subventions</p>
                    </div>
                    
                    {/* Card 3: Profil d'éligibilité */}
                    <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-slate-600">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="w-5 h-5 text-slate-300" />
                        <span className="text-slate-300 text-sm font-medium">Profil d'éligibilité</span>
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${
                          result.income_category === 'bleu' ? 'bg-blue-500' :
                          result.income_category === 'jaune' ? 'bg-yellow-500' :
                          result.income_category === 'violet' ? 'bg-violet-500' :
                          'bg-rose-500'
                        }`}>
                          {result.income_category?.toUpperCase() || 'N/A'}
                        </span>
                        <span className="text-2xl font-bold text-white">
                          {Math.round(result.summary?.taux_couverture || 0)}/100
                        </span>
                      </div>
                      <button className="text-lime-400 text-xs hover:underline">
                        Modifier mes revenus →
                      </button>
                    </div>
                  </div>

                  {/* 2. Détail des aides en 2 colonnes */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Colonne gauche: Subventions */}
                    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        <span className="text-white font-semibold">Subventions</span>
                        <span className="text-slate-400 text-xs ml-auto">versées directement</span>
                      </div>
                      
                      <div className="space-y-3">
                        {result.programs?.filter(p => p.funding_type === 'subvention').map((program, idx) => {
                          const isMaPrimeAdapt = program.program_name?.toLowerCase().includes('adapt');
                          return (
                            <div key={idx} className="flex items-center justify-between py-2">
                              <div className="flex items-center gap-2">
                                {isMaPrimeAdapt && <Heart className="w-4 h-4 text-indigo-400" />}
                                <span className={`text-sm ${isMaPrimeAdapt ? 'text-indigo-300' : 'text-slate-300'}`}>
                                  {program.program_name}
                                </span>
                                {program.eligibility_status === 'eligible' && (
                                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                )}
                              </div>
                              <span className={`font-semibold ${isMaPrimeAdapt ? 'text-indigo-300' : 'text-emerald-400'}`}>
                                {formatCurrency(program.amount)}
                              </span>
                            </div>
                          );
                        })}
                        
                        {/* Total subventions */}
                        <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-600">
                          <span className="text-white font-semibold">Total subventions</span>
                          <span className="text-xl font-bold text-emerald-400">
                            {formatCurrency(result.summary?.total_subventions || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Colonne droite: Avantages fiscaux */}
                    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-700">
                        <Award className="w-5 h-5 text-amber-400" />
                        <span className="text-white font-semibold">Avantages fiscaux</span>
                        <span className="text-slate-400 text-xs ml-auto">économie sur facture</span>
                      </div>
                      
                      <div className="space-y-3">
                        {result.programs?.filter(p => p.funding_type === 'avantage_fiscal').map((program, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-300">{program.program_name}</span>
                              {program.eligibility_status === 'eligible' && (
                                <CheckCircle2 className="w-3 h-3 text-amber-400" />
                              )}
                            </div>
                            <span className="font-semibold text-amber-400">
                              {formatCurrency(program.amount)}
                            </span>
                          </div>
                        ))}
                        
                        {/* Si pas d'avantages fiscaux dans programs, afficher le total */}
                        {(!result.programs?.some(p => p.funding_type === 'avantage_fiscal') && result.summary?.total_avantages_fiscaux > 0) && (
                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-300">TVA réduite 5,5%</span>
                              <CheckCircle2 className="w-3 h-3 text-amber-400" />
                            </div>
                            <span className="font-semibold text-amber-400">
                              {formatCurrency(result.summary?.total_avantages_fiscaux || 0)}
                            </span>
                          </div>
                        )}
                        
                        <p className="text-xs text-slate-500 pt-2">
                          Appliquée automatiquement sur la facture
                        </p>
                        
                        {/* Total avantages fiscaux */}
                        <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-600">
                          <span className="text-white font-semibold">Total avantages</span>
                          <span className="text-xl font-bold text-amber-400">
                            {formatCurrency(result.summary?.total_avantages_fiscaux || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3. Ligne de calcul */}
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 flex items-center justify-center gap-4 text-sm">
                    <span className="text-slate-300">
                      Coût travaux estimé: <strong className="text-white">{formatCurrency(result.summary?.estimated_work_cost || 0)}</strong>
                    </span>
                    <span className="text-slate-500">−</span>
                    <span className="text-emerald-400">
                      <strong>{formatCurrency(result.summary?.total_subventions || 0)}</strong> subventions
                    </span>
                    <span className="text-slate-500">=</span>
                    <span className="text-orange-400 font-bold text-lg">
                      {formatCurrency(result.summary?.reste_a_charge || 0)} à charge
                    </span>
                  </div>

                  {/* 4. Bloc Écrêtement (si appliqué) */}
                  {result.ecretement?.ecretement_applique && (
                    <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-xl p-5 border border-amber-500/40">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-5 h-5 text-amber-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-amber-300 mb-2">
                            Plafonnement des aides appliqué
                          </h3>
                          
                          <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                            <div className="bg-slate-900/50 rounded-lg p-2">
                              <p className="text-slate-400 text-xs">Plafond</p>
                              <p className="text-white font-semibold">{result.ecretement.plafond_label}</p>
                            </div>
                            <div className="bg-slate-900/50 rounded-lg p-2">
                              <p className="text-slate-400 text-xs">Montant max</p>
                              <p className="text-white font-semibold">{formatCurrency(result.ecretement.plafond_montant)}</p>
                            </div>
                            <div className="bg-slate-900/50 rounded-lg p-2">
                              <p className="text-slate-400 text-xs">Écrêté</p>
                              <p className="text-red-400 font-semibold">-{formatCurrency(result.ecretement.montant_ecrete)}</p>
                            </div>
                          </div>
                          
                          <p className="text-amber-200/70 text-xs">
                            {result.ecretement.explication}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 5. Prêts disponibles */}
                  {result.summary?.total_prets_disponibles > 0 && (
                    <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-xl p-4 border border-blue-500/30">
                      <div className="flex items-center gap-3">
                        <Banknote className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-blue-300 text-sm">Prêts à taux préférentiel disponibles</p>
                          <p className="text-lg font-bold text-blue-200">
                            Jusqu'à {formatCurrency(result.summary.total_prets_disponibles)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 6. Sections repliables */}
                  <div className="space-y-2">
                    <CollapsibleSection 
                      title="Comment fonctionne le plafonnement des aides ?" 
                      icon={HelpCircle}
                    >
                      <div className="space-y-3 text-sm">
                        <p>
                          Le cumul de toutes vos aides ne peut pas dépasser un certain pourcentage du coût des travaux.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            <span>Bleu : 100%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                            <span>Jaune : 80%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-violet-500"></span>
                            <span>Violet : 60%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                            <span>Rose : 40%</span>
                          </div>
                        </div>
                      </div>
                    </CollapsibleSection>
                    
                    <CollapsibleSection 
                      title="Qu'est-ce que le reste à charge ?" 
                      icon={HelpCircle}
                    >
                      <div className="space-y-2 text-sm">
                        <p>
                          {result.reste_a_charge_detail?.explication || 
                            "Le reste à charge correspond à ce que vous devez payer après déduction des aides."}
                        </p>
                        {(result.reste_a_charge_detail?.financable_par_pret || result.summary?.reste_a_charge > 0) && (
                          <div className="flex items-center gap-2 text-emerald-400 bg-emerald-900/20 rounded-lg p-2">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Finançable par l'Éco-PTZ (prêt à taux zéro)</span>
                          </div>
                        )}
                      </div>
                    </CollapsibleSection>
                  </div>

                  {/* 7. Pied de page avec catégorie */}
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm">Catégorie de revenus :</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                        result.income_category === 'bleu' ? 'bg-blue-500' :
                        result.income_category === 'jaune' ? 'bg-yellow-500' :
                        result.income_category === 'violet' ? 'bg-violet-500' :
                        'bg-rose-500'
                      }`}>
                        {result.income_category?.toUpperCase()}
                      </span>
                      <span className="text-slate-500 text-xs">
                        ({formatCurrency(parseInt(formData.income_rfr) || 0)} • {formData.household_size || 1} pers.)
                      </span>
                    </div>
                    <button className="text-lime-400 text-xs hover:underline flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" />
                      Vérifier mon éligibilité
                    </button>
                  </div>

                  {/* 8. Boutons d'action */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {/* TODO: Générer PDF */}}
                      className="flex-1 py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                      data-testid="download-pdf-btn"
                    >
                      <Download className="w-5 h-5" />
                      Télécharger PDF
                    </button>
                    
                    <button
                      onClick={() => { setResult(null); setError(null); }}
                      className="flex-1 py-3 px-4 bg-lime-600 hover:bg-lime-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                      data-testid="reset-btn"
                    >
                      <RotateCcw className="w-5 h-5" />
                      Nouvelle simulation
                    </button>
                  </div>
                </>
              )}

              {!result && !error && (
                <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700/50 flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-lime-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mb-4">
                    <Euro className="w-10 h-10 text-lime-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">Résultats de simulation</h3>
                  <p className="text-slate-400 text-sm max-w-md">
                    Remplissez le formulaire et sélectionnez les travaux envisagés pour calculer les aides disponibles
                  </p>
                  <div className="flex items-center gap-6 mt-6 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      MaPrimeRénov'
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Prime CEE
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Éco-PTZ
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'historique' && (
          <div className="bg-slate-800 rounded-xl border border-slate-700">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <History className="w-5 h-5 text-lime-400" />
                Historique des simulations
              </h2>
            </div>
            
            {history.length === 0 ? (
              <div className="p-12 text-center">
                <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Aucune simulation enregistrée</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {history.map((sim, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">
                          {sim.input?.work_types?.length || 0} travaux • Département {sim.input?.department || 'N/A'}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {new Date(sim.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lime-400 font-semibold">
                          {formatCurrency(sim.result?.summary?.total_subventions || 0)}
                        </p>
                        <p className="text-slate-500 text-sm">d'aides calculées</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulateurAidesDashboard;
